'use client'

import { LineCloseCircle } from '@qr-front/icons/line-close-circle'
import * as DialogPrimitive from '@radix-ui/react-dialog'
import * as React from 'react'
import { Fragment, ReactNode } from 'react'

import { cn } from '@/utils'

const Dialog = DialogPrimitive.Root

const DialogTrigger = DialogPrimitive.Trigger

const DialogPortal = DialogPrimitive.Portal

const DialogClose = DialogPrimitive.Close

const DialogOverlay = React.forwardRef<
	React.ElementRef<typeof DialogPrimitive.Overlay>,
	React.ComponentPropsWithoutRef<typeof DialogPrimitive.Overlay>
>(({ className, ...props }, ref) => (
	<DialogPrimitive.Overlay
		ref={ref}
		className={cn(
			'fixed inset-0 z-50 bg-popup-overlay data-[state=open]:animate-in data-[state=closed]:animate-out data-[state=closed]:fade-out-0 data-[state=open]:fade-in-0',
			className,
		)}
		{...props}
	/>
))
DialogOverlay.displayName = DialogPrimitive.Overlay.displayName

const DialogContent = React.forwardRef<
	React.ElementRef<typeof DialogPrimitive.Content>,
	Omit<
		React.ComponentPropsWithoutRef<typeof DialogPrimitive.Content>,
		'className'
	> & {
		closeButtonLabel: string
		closeButtonPlacement?: 'outside' | 'inline'
		usePortal?: boolean
		CustomPortal?: (props: { children: ReactNode }) => ReactNode
		scrollAreaClassName?: string
		overlayClassName?: string
		toastContainer?: ReactNode
		className?: string
		panelClassName?: string
	}
>(
	(
		{
			closeButtonLabel,
			closeButtonPlacement = 'outside',
			usePortal = true,
			CustomPortal,
			overlayClassName,
			className,
			panelClassName,
			scrollAreaClassName,
			children,
			toastContainer,
			...props
		},
		ref,
	) => {
		const Wrapper = CustomPortal || (usePortal && DialogPortal) || Fragment
		const isInlineClose = closeButtonPlacement === 'inline'
		const shouldRenderCustomPanel = !!panelClassName || !!toastContainer

		const closeButton = (
			<DialogPrimitive.Close
				className={cn(
					'flex items-center gap-2 text-white',
					isInlineClose
						? 'w-full shrink-0 justify-end py-2'
						: 'absolute -top-8 right-0',
				)}
				data-testid="dialog-close-button"
			>
				<span className="type-body-input-title">
					{closeButtonLabel}
				</span>
				<LineCloseCircle className="size-6 shrink-0" />
			</DialogPrimitive.Close>
		)

		return (
			<Wrapper>
				{CustomPortal ? (
					<div className="absolute inset-0 rounded-[theme(spacing.7)] z-auto h-full w-full bg-popup-overlay data-[state=open]:animate-in data-[state=closed]:animate-out data-[state=closed]:fade-out-0 data-[state=open]:fade-in-0" />
				) : (
					<DialogOverlay className={overlayClassName} />
				)}

				<DialogPrimitive.Content
					ref={ref}
					className={cn(
						shouldRenderCustomPanel
							? 'pointer-events-none fixed inset-0 z-50'
							: 'fixed left-1/2 top-1/2 z-50 flex max-h-[calc(100dvh-4rem)] w-full max-w-[min(500px,calc(100dvw-2.5rem))] -translate-x-1/2 -translate-y-1/2 flex-col rounded-10 bg-white p-8 duration-200 data-[state=open]:animate-in data-[state=closed]:animate-out data-[state=closed]:fade-out-0 data-[state=open]:fade-in-0 data-[state=closed]:zoom-out-95 data-[state=open]:zoom-in-95 data-[state=closed]:slide-out-to-left-1/2 data-[state=closed]:slide-out-to-top-[48%] data-[state=open]:slide-in-from-left-1/2 data-[state=open]:slide-in-from-top-[48%]',
						CustomPortal &&
							!shouldRenderCustomPanel &&
							'absolute z-auto max-w-[min(500px,calc(100%-2.5rem))]',
						className,
					)}
					{...props}
				>
					{shouldRenderCustomPanel ? (
						<>
							{toastContainer}

							<div
								className={cn(
									'pointer-events-auto flex flex-col',
									'rounded-10 bg-white',
									'fixed left-1/2 top-1/2 p-8',
									'-translate-x-1/2 -translate-y-1/2',
									'w-full max-w-[min(500px,calc(100dvw-2.5rem))]',
									'max-h-[calc(100dvh-4rem)]',
									'duration-200',
									'data-[state=open]:animate-in data-[state=closed]:animate-out',
									'data-[state=closed]:fade-out-0 data-[state=open]:fade-in-0',
									'data-[state=closed]:zoom-out-95 data-[state=open]:zoom-in-95',
									'data-[state=closed]:slide-out-to-left-1/2 data-[state=closed]:slide-out-to-top-[48%]',
									'data-[state=open]:slide-in-from-left-1/2 data-[state=open]:slide-in-from-top-[48%]',
									CustomPortal &&
										'absolute z-auto max-w-[min(500px,calc(100%-2.5rem))]',
									panelClassName,
								)}
							>
								{isInlineClose && closeButton}
								<div
									className={cn(
										'overflow-x-hidden rounded-[inherit]',
										isInlineClose
											? 'min-h-0 flex-1 overflow-y-auto'
											: 'overflow-y-auto',
										scrollAreaClassName,
									)}
								>
									{children}
								</div>
								{!isInlineClose && closeButton}
							</div>
						</>
					) : (
						<>
							{isInlineClose && closeButton}
							<div
								className={cn(
									'overflow-y-auto overflow-x-hidden',
									isInlineClose && 'min-h-0 flex-1',
									scrollAreaClassName,
								)}
							>
								{children}
							</div>
							{!isInlineClose && closeButton}
						</>
					)}
				</DialogPrimitive.Content>
			</Wrapper>
		)
	},
)

DialogContent.displayName = DialogPrimitive.Content.displayName

const DialogHeader = ({
	className,
	...props
}: React.HTMLAttributes<HTMLDivElement>) => (
	<div className={cn('flex flex-col text-center', className)} {...props} />
)
DialogHeader.displayName = 'DialogHeader'

const DialogFooter = ({
	className,
	...props
}: React.HTMLAttributes<HTMLDivElement>) => (
	<div
		className={cn(
			'flex flex-wrap gap-6 *:grow *:[flex-basis:0]',
			className,
		)}
		{...props}
	/>
)
DialogFooter.displayName = 'DialogFooter'

const DialogTitle = React.forwardRef<
	React.ElementRef<typeof DialogPrimitive.Title>,
	React.ComponentPropsWithoutRef<typeof DialogPrimitive.Title>
>(({ className, ...props }, ref) => (
	<DialogPrimitive.Title
		ref={ref}
		className={cn('type-heading-3', className)}
		{...props}
	/>
))
DialogTitle.displayName = DialogPrimitive.Title.displayName

const DialogDescription = React.forwardRef<
	React.ElementRef<typeof DialogPrimitive.Description>,
	React.ComponentPropsWithoutRef<typeof DialogPrimitive.Description>
>(({ className, ...props }, ref) => (
	<DialogPrimitive.Description
		ref={ref}
		className={cn('text-body-medium', className)}
		{...props}
	/>
))
DialogDescription.displayName = DialogPrimitive.Description.displayName

export {
	Dialog,
	DialogClose,
	DialogContent,
	DialogDescription,
	DialogFooter,
	DialogHeader,
	DialogOverlay,
	DialogPortal,
	DialogTitle,
	DialogTrigger,
}
